import { ValidationOptions } from 'react-hook-form';
import { Column } from 'material-table';
import plLocale from 'date-fns/locale/pl';
import enLocale from 'date-fns/locale/en-US';
import format from 'date-fns/format';

export enum UserRole {
	SuperAdmin = 'SuperAdmin',
	DealerAdmin = 'DealerAdmin',
	CorporationAdmin = 'CorporationAdmin',
	LocationAdmin = 'LocationAdmin',
	None = '',
}

export const UserRoles = {
	any: [UserRole.SuperAdmin, UserRole.DealerAdmin, UserRole.CorporationAdmin, UserRole.LocationAdmin],
	atLeastLocationAdmin: [UserRole.SuperAdmin, UserRole.DealerAdmin, UserRole.CorporationAdmin, UserRole.LocationAdmin],
	atLeastCorporationAdmin: [UserRole.SuperAdmin, UserRole.DealerAdmin, UserRole.CorporationAdmin],
	atLeastDealerAdmin: [UserRole.SuperAdmin, UserRole.DealerAdmin],
	allExceptSuperAdmin: [UserRole.DealerAdmin, UserRole.CorporationAdmin, UserRole.LocationAdmin],
};

export const getHighestUserRole = (roles: string[]): string => {
	for (let role in UserRole) {
		if (roles.findIndex((r) => r === role) >= 0) return role;
	}
	return UserRole.None;
};

export const colors = { default: '#1774FE', clicked: '#999999', main: '#32A3F5', barPanelSelectItemColor: 'rgba(50, 163, 245, 0.2)' };
export const dateFormat = 'dd/MM/yyyy';
export const yearMonthDayFormat = 'YYYY-MM-DD';
export const dateFormatWithDots = 'dd.MM.yyyy';
export const dateTimeFormat = 'yyyy/MM/dd, HH:mm';

export const getValidator = (
	translation: any,
	max: number | null,
	pattern: { value: RegExp; message: string } | null,
	required: boolean = false,
	minLength: number = 2
) => {
	const registerObject: ValidationOptions = {};

	if (max)
		registerObject.maxLength = {
			value: max,
			message:
				max !== 64
					? translation('common.validations.maxLength', { length: max })
					: translation('common.validations.maxLengthVariety', { length: max }),
		};

	if (required) {
		if (max) {
			registerObject.minLength = {
				value: minLength,
				message:
					minLength < 5
						? translation('common.validations.minLength', { length: minLength })
						: translation('common.validations.minLengthVariety', { length: minLength }),
			};
		}
		registerObject.required = {
			value: true,
			message: translation('common.validations.required'),
		};
	}

	if (pattern) registerObject.pattern = pattern;
	return registerObject;
};

export function debounce(func: any, wait: any, immediate: any = null) {
	var timeout: any;
	return function () {
		//@ts-ignore
		var context: any = this,
			args = arguments;
		var later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
}

export function isObject(item: any) {
	return item && typeof item === 'object' && !Array.isArray(item);
}

export function mergeDeep(target: any, ...sources: any): any {
	if (!sources.length) return target;
	const source = sources.shift();

	if (isObject(target) && isObject(source)) {
		for (const key in source) {
			if (isObject(source[key])) {
				if (!target[key]) Object.assign(target, { [key]: {} });
				mergeDeep(target[key], source[key]);
			} else {
				Object.assign(target, { [key]: source[key] });
			}
		}
	}

	return mergeDeep(target, ...sources);
}

export interface onSubmitProps {
	presetDataCallback?: any;
	setModalOpen: any;
	api: any;
	setId?: any;
	setEntity?: any;
	setOnSaveClickHandler?: (data: any) => void;
	refreshTable?: () => void;
	setOnSuccessSubmit?: (data: any) => void;
}
export interface ModalSubmitProps extends onSubmitProps {
	useSnackbar: any;
	useTranslation: any;
	appContext: any;
	refreshTable?: () => void;
}

export const genericOnSubmit = async (data: any, callbacks: ModalSubmitProps) => {
	callbacks.appContext.setLoaderVisible(true);

	if (typeof callbacks.presetDataCallback === 'function') data = callbacks.presetDataCallback(data);

	data = removeNullOrWhiteSpaceProperties(data);

	if (data === null || data === undefined) {
		callbacks.appContext.setLoaderVisible(false);
		callbacks.setModalOpen(false);
		return;
	}

	let response: any;
	let isNew = !data.id;
	try {
		if (isNew) {
			response = await callbacks.api.Add(data, data?.dictType);
		} else {
			response = await callbacks.api.Update(data, data?.dictType);
		}

		if (response.success) {
			callbacks.setId && callbacks.setId(response.data);
			if (!isNew && callbacks.setEntity) callbacks.setEntity(data);
			const x = `${isNew ? callbacks.useTranslation('common.dataSaved') : callbacks.useTranslation('common.changesSaved')}`;

			callbacks.useSnackbar(x, { variant: 'success' });
			callbacks.setModalOpen(false);
		} else {
			let message = callbacks.useTranslation(`errorCodes.${response.errors[0].errorCode}`);
			callbacks.useSnackbar(`${callbacks.useTranslation('common.errorOccured')}: ${message}`, { variant: 'error' });
		}
	} catch (e) {
		callbacks.useSnackbar(`${callbacks.useTranslation('common.errorOccured')}: ${e.message}`, { variant: 'error' });
	}

	callbacks.appContext.setLoaderVisible(false);
};

export function removeNullOrWhiteSpaceProperties(data: any) {
	Object.keys(data).forEach(function (item) {
		if (typeof data[item] === 'string') {
			data[item] = data[item].trim();
			if (data[item] === '') delete data[item];
		}
	});
	return data;
}

export function formatPrice(price: number | string, currency: string | null = null, separator = ',') {
	if (price === null || price === undefined) {
		return '';
	}

	let p = Number(price);
	if (price.toString().indexOf(',') !== -1) {
		p = Number(price.toString().replace(',', '.'));
	}
	const formattedPrice = p.toFixed(2).replace('.', separator);

	return currency ? `${formattedPrice} ${currency}` : formattedPrice;
}

export function adjustTable(headers: Array<Column<any>>, isInDetails: boolean = false): Array<Column<any>> {
	const headersCount = headers.filter((header) => !header.grouping && !header.hidden).length;
	//  260px = 240px (drawer width) + 20px (vertical scrollbar width)
	//  272px = 240px (drawer width) + 20px (vertical scrollbar width) + 16px (detials page padding)
	const offset = isInDetails ? '356px' : '320px';
	const value = `calc((100vw - ${offset}) / ${headersCount})`;

	headers.forEach(
		(header) =>
			(header.cellStyle = {
				...header.cellStyle,
				maxWidth: value,
				width: value,
				paddingTop: '10px',
				paddingBottom: '10px',
				paddingLeft: '30px',
				paddingRight: '30px',
				wordWrap: 'break-word',
			})
	);

	headers.forEach(
		(header) =>
			(header.headerStyle = {
				...header.headerStyle,
				paddingLeft: '30px',
				paddingRight: '30px',
				maxWidth: value,
				width: value,
				wordWrap: 'break-word',
			})
	);

	return headers;
}

export function convertBytesToMbsOrKbs(filesize: number) {
	var size = '';

	if (filesize >= 1000000) {
		size = filesize / 1000000 + ' MBs';
	} else if (filesize >= 1000) {
		size = filesize / 1000 + ' KBs';
	} else {
		size = filesize + ' Bs';
	}

	return size;
}

export function numberWithSpaces(x: number | string) {
	let input = typeof x === 'number' ? x.toString() : x;
	let parts = input.replace(/ /g, '').split('.');

	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	return parts.join('.').replace('.', ',');
}

export const imageDimensions = {
	mainGoalImage: {
		width: 465,
		height: 217,
	},
	additionalGoalImage: {
		width: 128,
		height: 160,
	},
	mainImage: {
		width: 1920,
		height: 1080,
	},
	aboutUsImage: {
		width: 1920,
		height: 1080,
	},
	logo: {
		width: 200,
		height: 200,
	},
	sidePicture: {
		width: 650,
		height: 1080,
	},
};

export const blobToFile = (theBlob: Blob, fileName: string): File => {
	let b: any = theBlob;

	b.lastModifiedDate = new Date();
	b.name = fileName;

	return theBlob as File;
};

export enum DictReportType {
	RealTime = 'RealTime',
	Daily = 'Daily',
	Monthly = 'Monthly',
}

export function DictReportTypeLabel(t: any): Map<string, string> {
	return new Map<string, string>([
		[DictReportType.RealTime, t('report.type.realTime')],
		[DictReportType.Daily, t('report.type.daily')],
		[DictReportType.Monthly, t('report.type.monthly')],
	]);
}

export const locale = navigator.language === 'pl' ? plLocale : enLocale;

export const formatPhoneNumber = (number: string, countryCode?: string) => {
	if (!countryCode) {
		return `${
			number
				.replace(/\s+/g, '')
				.match(/.{1,3}/g)
				?.join(' ') ?? ''
		}`;
	}

	return `+${String(countryCode).replace(/\++/g, '')} ${number
		.replace(/\s+/g, '')
		.match(/.{1,3}/g)
		?.join(' ')}`;
};

export function parseIsoTimeStringToLocalTime(IsoString: string) {
	const date = new Date(IsoString);

	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()));
}

export function formatIsoDateString(date: string | Date | undefined) {
	console.log(date);
	if (date) {
		var newDate: Date = parseIsoTimeStringToLocalTime(date.toString());
		return format(newDate, dateTimeFormat, { locale: plLocale });
	}

	return 'n/a';
}
